import { useTranslation } from "react-i18next";
import { Check } from "@untitledui/icons";
import { cx } from "@/utils/cx";
import { useThemeStore, type ThemeMode } from "@/stores/themeStore";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { SUPPORTED_LOCALES, useChangeLocale, type AppLocale } from "@/lib/locale";

/**
 * Nastavení → Vzhled — výběr motivu (Systém / Světlý / Tmavý) jako UUI karty
 * s miniaturním náhledem a volba jazyka rozhraní.
 *
 * Motiv řídí `useThemeStore` (přepíná `.dark` na <html>, perzistuje do localStorage),
 * jazyk se naopak ukládá na server k účtu — platí i pro texty renderované backendem.
 * Obojí zrcadlí rychlé přepínače v patičce sidebaru.
 */

/** Mini náhled UI v daném motivu — pevné barvy (ukazuje cílový motiv nezávisle na aktuálním). */
function Mock({ variant }: { variant: "light" | "dark" }) {
  const c =
    variant === "light"
      ? { bg: "#ffffff", side: "#f4f4f5", line: "#e4e4e7", lineSoft: "#f1f1f3", accent: "#7f56d9" }
      : { bg: "#18181b", side: "#27272a", line: "#3f3f46", lineSoft: "#2a2a2e", accent: "#9b8afb" };
  return (
    <div className="flex h-full w-full" style={{ backgroundColor: c.bg }}>
      <div className="flex w-1/4 flex-col gap-1.5 p-2" style={{ backgroundColor: c.side }}>
        <div className="h-2 w-2/3 rounded-full" style={{ backgroundColor: c.accent }} />
        <div className="h-1.5 w-full rounded-full" style={{ backgroundColor: c.line }} />
        <div className="h-1.5 w-5/6 rounded-full" style={{ backgroundColor: c.line }} />
        <div className="h-1.5 w-full rounded-full" style={{ backgroundColor: c.line }} />
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-2.5">
        <div className="h-2.5 w-1/2 rounded-full" style={{ backgroundColor: c.line }} />
        <div className="h-1.5 w-full rounded-full" style={{ backgroundColor: c.lineSoft }} />
        <div className="h-1.5 w-full rounded-full" style={{ backgroundColor: c.lineSoft }} />
        <div className="h-1.5 w-4/5 rounded-full" style={{ backgroundColor: c.lineSoft }} />
      </div>
    </div>
  );
}

function Preview({ mode }: { mode: ThemeMode }) {
  const box = "aspect-[16/10] w-full overflow-hidden";
  if (mode === "light") return <div className={box}><Mock variant="light" /></div>;
  if (mode === "dark") return <div className={box}><Mock variant="dark" /></div>;
  // system — split light/dark
  return (
    <div className={cx(box, "flex")}>
      <div className="w-1/2 overflow-hidden border-r border-black/10"><div className="w-[200%] h-full"><Mock variant="light" /></div></div>
      <div className="w-1/2 overflow-hidden"><div className="-translate-x-1/2 w-[200%] h-full"><Mock variant="dark" /></div></div>
    </div>
  );
}

const OPTIONS: ThemeMode[] = ["system", "light", "dark"];

export function AppearancePage() {
  const { t, i18n } = useTranslation();
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const changeLocale = useChangeLocale();
  const activeLocale = i18n.language as AppLocale;

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("appearance.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("appearance.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {OPTIONS.map((value) => {
          const active = mode === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              onClick={() => setMode(value)}
              className={cx(
                "group flex flex-col gap-3 rounded-2xl bg-primary p-1.5 text-left shadow-xs ring-1 transition",
                active ? "ring-2 ring-brand" : "ring-secondary hover:ring-primary",
              )}
            >
              <div className="overflow-hidden rounded-xl ring-1 ring-secondary">
                <Preview mode={value} />
              </div>
              <div className="flex items-center justify-between gap-2 px-2.5 pb-1.5">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-primary">{t(`appearance.${value}`)}</p>
                  <p className="truncate text-xs text-tertiary">{t(`appearance.${value}.desc`)}</p>
                </div>
                <span
                  className={cx(
                    "flex size-5 shrink-0 items-center justify-center rounded-full ring-1 transition",
                    active ? "bg-brand-solid ring-transparent" : "bg-primary ring-primary",
                  )}
                >
                  {active && <Check className="size-3.5 text-white" />}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("locale.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("locale.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SUPPORTED_LOCALES.map((lang) => {
          const active = activeLocale === lang;
          return (
            <button
              key={lang}
              type="button"
              aria-pressed={active}
              disabled={changeLocale.isPending}
              onClick={() => changeLocale.mutate(lang)}
              className={cx(
                "flex items-center justify-between gap-3 rounded-2xl bg-primary px-4 py-3.5 text-left shadow-xs ring-1 transition disabled:opacity-60",
                active ? "ring-2 ring-brand" : "ring-secondary hover:ring-primary",
              )}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="text-lg font-semibold text-tertiary">{t(`locale.${lang}.short`)}</span>
                <p className="truncate text-sm font-semibold text-primary">{t(`locale.${lang}`)}</p>
              </div>
              <span
                className={cx(
                  "flex size-5 shrink-0 items-center justify-center rounded-full ring-1 transition",
                  active ? "bg-brand-solid ring-transparent" : "bg-primary ring-primary",
                )}
              >
                {active && <Check className="size-3.5 text-white" />}
              </span>
            </button>
          );
        })}
      </div>
      {changeLocale.isError && <p className="text-sm text-error-primary">{t("locale.saveError")}</p>}
    </div>
  );
}
